import React, { useCallback, useEffect, useMemo, useState } from "react";
import AdminLayout from "../../components/AdminLayout";
import { getCaptureQosSummary } from "../../components/funtionsBD/Mainservice";
import { setCaptureQosRunId } from "../../utils/captureQos";
import "./BusinessIntelligence.css";

function hoursAgoIso(hours) {
  return new Date(Date.now() - hours * 3600 * 1000).toISOString();
}

function pct(rate) {
  if (rate == null || Number.isNaN(Number(rate))) return "—";
  return `${Math.round(Number(rate) * 1000) / 10}%`;
}

function ms(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  if (n >= 10_000) return `${Math.round(n / 1000)}s`;
  return `${Math.round(n)}ms`;
}

function age(n) {
  if (n == null) return "—";
  if (n < 60_000) return `${Math.round(n / 1000)}s`;
  return `${Math.round(n / 60_000)}m`;
}

function newRunId() {
  const d = new Date();
  const stamp = d.toISOString().replace(/[-:TZ.]/g, "").slice(0, 14);
  const rand = Math.random().toString(36).slice(2, 8);
  return `qos-${stamp}-${rand}`;
}

export function CaptureQosDashboard() {
  const [hours, setHours] = useState(24);
  const [runId, setRunId] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [draftRun, setDraftRun] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    const from = hoursAgoIso(hours);
    getCaptureQosSummary({
      from,
      runId: runId.trim() || undefined,
    })
      .then((res) => setData(res?.data?.data || null))
      .catch((err) => {
        setData(null);
        setError(err?.message || "Failed to load Capture QoS");
      })
      .finally(() => setLoading(false));
  }, [hours, runId]);

  useEffect(() => {
    load();
  }, [load]);

  const slo = data?.slo || {};
  const capacity = data?.capacity || {};
  const clients = data?.clients || [];

  const joinHint = useMemo(() => {
    const id = (runId || draftRun || "").trim();
    if (!id) return null;
    const origin =
      typeof window !== "undefined" ? window.location.origin : "https://app…";
    return {
      live: `${origin}/live-training?captureQosRun=${encodeURIComponent(id)}`,
      match: `${origin}/competition/match/<matchId>?captureQosRun=${encodeURIComponent(id)}`,
      id,
    };
  }, [runId, draftRun]);

  const createRun = () => {
    const id = newRunId();
    setDraftRun(id);
    setRunId(id);
    setCaptureQosRunId(id);
  };

  return (
    <AdminLayout>
      <div className="bi-root">
        <div className="bi-pad">
          <header className="bi-hero">
            <div>
              <p className="bi-hero__eyebrow">Capture QoS</p>
              <h1 className="bi-hero__title">Video pipeline</h1>
              <p className="bi-hero__sub">
                Encode/upload quality and capacity signals across web, iOS, and
                Android. Filter by harness <code>run_id</code> for simultaneous
                multi-client validation.
              </p>
            </div>
            <div className="bi-hero__stat">
              <span className="bi-hero__stat-k">Events</span>
              <span className="bi-hero__stat-v">{data?.event_count ?? "—"}</span>
            </div>
          </header>

          <section className="bi-panel" style={{ marginBottom: 16 }}>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 12,
                alignItems: "flex-end",
              }}
            >
              <label style={{ display: "grid", gap: 4 }}>
                <span className="bi-hero__stat-k">Window</span>
                <select
                  value={hours}
                  onChange={(e) => setHours(Number(e.target.value))}
                >
                  <option value={6}>Last 6h</option>
                  <option value={24}>Last 24h</option>
                  <option value={72}>Last 72h</option>
                  <option value={168}>Last 7d</option>
                </select>
              </label>
              <label style={{ display: "grid", gap: 4, flex: "1 1 220px" }}>
                <span className="bi-hero__stat-k">Harness run_id</span>
                <input
                  value={runId}
                  onChange={(e) => setRunId(e.target.value)}
                  placeholder="optional — filter clients in one run"
                />
              </label>
              <button type="button" className="bi-btn" onClick={createRun}>
                New run id
              </button>
              <button
                type="button"
                className="bi-btn bi-btn--primary"
                onClick={load}
                disabled={loading}
              >
                {loading ? "Loading…" : "Refresh"}
              </button>
            </div>
            {joinHint ? (
              <div style={{ marginTop: 12, fontSize: "0.85rem" }}>
                <p style={{ margin: "0 0 6px" }}>
                  Join clients with <code>?captureQosRun={joinHint.id}</code>:
                </p>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  <li>
                    Live training: <code>{joinHint.live}</code>
                  </li>
                  <li>
                    Championship: <code>{joinHint.match}</code>
                  </li>
                </ul>
              </div>
            ) : null}
            {data?.run_ids?.length ? (
              <p style={{ margin: "10px 0 0", fontSize: "0.8rem" }}>
                Recent runs:{" "}
                {data.run_ids.slice(0, 8).map((id) => (
                  <button
                    key={id}
                    type="button"
                    className="bi-chip"
                    style={{ marginRight: 6 }}
                    onClick={() => setRunId(id)}
                  >
                    {id}
                  </button>
                ))}
              </p>
            ) : null}
          </section>

          {error ? (
            <p className="bi-error" role="alert">
              {error}
            </p>
          ) : null}

          <section className="bi-grid bi-grid--3" style={{ marginBottom: 16 }}>
            <div className="bi-panel">
              <h2 className="bi-panel__title">Upload SLO</h2>
              <p className="bi-metric">
                {pct(slo.upload_success_rate)}
              </p>
              <p className="bi-muted">
                ok {slo.upload_ok ?? 0} / fail {slo.upload_fail ?? 0}
              </p>
              <p className="bi-muted">
                p50 {ms(slo.upload_p50_ms)} · p95 {ms(slo.upload_p95_ms)}
              </p>
            </div>
            <div className="bi-panel">
              <h2 className="bi-panel__title">Encode</h2>
              <p className="bi-metric">{slo.encode_fail ?? 0}</p>
              <p className="bi-muted">encode_fail count</p>
              <p className="bi-muted">
                p50 {ms(slo.encode_p50_ms)} · p95 {ms(slo.encode_p95_ms)}
              </p>
              <p className="bi-muted">
                bitrate avg{" "}
                {slo.bitrate_effective_avg != null
                  ? `${Math.round(slo.bitrate_effective_avg / 1000)} kbps`
                  : "—"}
              </p>
            </div>
            <div className="bi-panel">
              <h2 className="bi-panel__title">FPS fidelity</h2>
              <p className="bi-metric">
                {slo.fps_fidelity != null
                  ? pct(slo.fps_fidelity)
                  : "—"}
              </p>
              <p className="bi-muted">actual / requested (avg)</p>
              <p className="bi-muted">
                recording_stop {slo.recording_stops ?? 0}
              </p>
            </div>
          </section>

          <section className="bi-grid bi-grid--3" style={{ marginBottom: 16 }}>
            <div className="bi-panel">
              <h2 className="bi-panel__title">Concurrent uploads</h2>
              <p className="bi-metric">
                {capacity.concurrent_uploads ?? 0}
              </p>
              <p className="bi-muted">in-flight on this API process</p>
            </div>
            <div className="bi-panel">
              <h2 className="bi-panel__title">Pose queue</h2>
              <p className="bi-metric">
                {capacity.pose?.queued ?? "—"}
              </p>
              <p className="bi-muted">
                processing {capacity.pose?.processing ?? "—"} · age{" "}
                {age(capacity.pose?.oldest_age_ms)} · conc{" "}
                {capacity.pose?.concurrency ?? "—"}
              </p>
            </div>
            <div className="bi-panel">
              <h2 className="bi-panel__title">Reencode queue</h2>
              <p className="bi-metric">
                {capacity.reencode?.queued ?? "—"}
              </p>
              <p className="bi-muted">
                processing {capacity.reencode?.processing ?? "—"} · age{" "}
                {age(capacity.reencode?.oldest_age_ms)} · conc{" "}
                {capacity.reencode?.concurrency ?? "—"}
              </p>
            </div>
          </section>

          <section className="bi-panel" style={{ marginBottom: 16 }}>
            <h2 className="bi-panel__title">Clients (side-by-side)</h2>
            <div style={{ overflowX: "auto" }}>
              <table className="bi-table">
                <thead>
                  <tr>
                    <th>Platform</th>
                    <th>OS / browser</th>
                    <th>Events</th>
                    <th>Upload ok/fail</th>
                    <th>p95 upload</th>
                    <th>Mbps</th>
                    <th>Bitrate</th>
                    <th>Heap max</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.length === 0 ? (
                    <tr>
                      <td colSpan={8}>No client events in this window.</td>
                    </tr>
                  ) : (
                    clients.map((c) => (
                      <tr key={`${c.platform}-${c.ua_hash}-${c.user_id}`}>
                        <td>{c.platform}</td>
                        <td>
                          {c.os || "—"} / {c.browser || "—"}
                        </td>
                        <td>{c.events}</td>
                        <td>
                          {c.upload_ok}/{c.upload_fail}
                        </td>
                        <td>{ms(c.upload_p95_ms)}</td>
                        <td>{c.mbps_avg ?? "—"}</td>
                        <td>
                          {c.bitrate_effective_avg != null
                            ? `${Math.round(c.bitrate_effective_avg / 1000)}k`
                            : "—"}
                        </td>
                        <td>
                          {c.js_heap_max_mb != null
                            ? `${c.js_heap_max_mb} MB`
                            : "—"}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="bi-panel">
            <h2 className="bi-panel__title">Recent events</h2>
            <div style={{ overflowX: "auto" }}>
              <table className="bi-table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Event</th>
                    <th>Flow</th>
                    <th>Platform</th>
                    <th>Run</th>
                    <th>ms</th>
                    <th>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.recent || []).length === 0 ? (
                    <tr>
                      <td colSpan={7}>No events yet.</td>
                    </tr>
                  ) : (
                    (data.recent || []).map((ev, i) => (
                      <tr key={`${ev.occurred_at}-${ev.event}-${i}`}>
                        <td>
                          {ev.occurred_at
                            ? new Date(ev.occurred_at).toLocaleString()
                            : "—"}
                        </td>
                        <td>{ev.event}</td>
                        <td>{ev.flow}</td>
                        <td>{ev.platform}</td>
                        <td>{ev.run_id || "—"}</td>
                        <td>{ms(ev.upload_ms)}</td>
                        <td>{ev.error_class || (ev.ok === false ? "fail" : "")}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>
    </AdminLayout>
  );
}

export default CaptureQosDashboard;
